// Product entries (e-commerce sites): one knowledge entry per product, stored
// as labelled text so it reads well to people and to the model, and so the
// chunker keeps a product in one chunk. The product page URL is the entry's
// source URL, so an answer about it can link the page ("Read more"). Pure;
// the admin product form round-trips through formatProduct / parseProduct.

export interface Product {
  name: string;
  description: string;
  price: string;
  url: string;
  sku: string;
  category: string;
  availability: string;
  /** Other attributes (colour, size, brand…). */
  details: Array<{ label: string; value: string }>;
}

export const MAX_PRODUCTS_PER_IMPORT = 500;

const FIELDS: Array<[keyof Omit<Product, 'name' | 'description' | 'details'>, string]> = [
  ['price', 'Price'],
  ['sku', 'SKU'],
  ['category', 'Category'],
  ['availability', 'Availability'],
  ['url', 'Link'],
];

export function emptyProduct(): Product {
  return { name: '', description: '', price: '', url: '', sku: '', category: '', availability: '', details: [] };
}

/** The entry text for a product. */
export function formatProduct(p: Product): string {
  const lines = [`Product: ${p.name.trim()}`];
  for (const [key, label] of FIELDS) {
    const v = p[key].trim();
    if (v) lines.push(`${label}: ${v}`);
  }
  for (const d of p.details) {
    if (d.label.trim() && d.value.trim()) lines.push(`${d.label.trim()}: ${d.value.trim()}`);
  }
  const description = p.description.trim();
  return description ? `${lines.join('\n')}\n\n${description}` : lines.join('\n');
}

/** Read an entry's text back into fields (labels it doesn't know become details). */
export function parseProduct(content: string): Product {
  const p = emptyProduct();
  const [head, ...rest] = content.replace(/\r\n?/g, '\n').split(/\n\s*\n/);
  for (const line of (head ?? '').split('\n')) {
    const m = /^([^:]{1,60}):\s*(.*)$/.exec(line);
    if (!m) continue;
    const label = m[1]!.trim();
    const value = m[2]!.trim();
    if (/^product$/i.test(label)) p.name = value;
    else {
      const field = FIELDS.find(([, l]) => l.toLowerCase() === label.toLowerCase());
      if (field) p[field[0]] = value;
      else p.details.push({ label, value });
    }
  }
  p.description = rest.join('\n\n').trim();
  return p;
}
